import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, DollarSign, Activity, Calendar, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const MerchantAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const fetchAnalytics = async (days) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/merchant/analytics?days=${days}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(period);
  }, [period]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading || !analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const growthIsPositive = analytics.growth.revenue_growth_percent >= 0;

  return (
    <div className="space-y-6">
      {/* Selector de Período */}
      <div className="flex justify-end gap-2 mb-4">
        {[7, 15, 30, 60].map(days => (
          <button
            key={days}
            onClick={() => setPeriod(days)}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              period === days
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {days}d
          </button>
        ))}
      </div>

      {/* Métricas Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Ingresos Totales */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">Ingresos</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(analytics.summary.total_revenue)}</p>
          <p className="text-sm opacity-75 mt-1">Últimos {period} días</p>
        </div>

        {/* Transacciones */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-8 h-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">Transacciones</span>
          </div>
          <p className="text-3xl font-bold">{analytics.summary.paid_orders}</p>
          <p className="text-sm opacity-75 mt-1">
            Tasa de éxito: {analytics.summary.conversion_rate}%
          </p>
        </div>

        {/* Ticket Promedio */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Calendar className="w-8 h-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">Ticket Promedio</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(analytics.summary.avg_order_value)}</p>
          <p className="text-sm opacity-75 mt-1">Por transacción</p>
        </div>

        {/* Comisiones Pagadas */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 opacity-80" />
            <span className="text-sm font-medium opacity-90">Comisiones</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(analytics.summary.total_commission_paid)}</p>
          <p className="text-sm opacity-75 mt-1">Total pagado</p>
        </div>
      </div>

      {/* Comparación con Período Anterior */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Comparación con Período Anterior</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Período Actual</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(analytics.growth.current_period_revenue)}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Período Anterior</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(analytics.growth.previous_period_revenue)}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">Crecimiento</p>
              <div className={`flex items-center justify-center space-x-2 ${growthIsPositive ? 'text-green-600' : 'text-red-600'}`}>
                {growthIsPositive ? (
                  <ArrowUpRight className="w-12 h-12" />
                ) : (
                  <ArrowDownRight className="w-12 h-12" />
                )}
                <span className="text-5xl font-bold">
                  {Math.abs(analytics.growth.revenue_growth_percent).toFixed(1)}%
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {growthIsPositive ? 'Incremento' : 'Disminución'} vs período anterior
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Métodos de Pago Preferidos */}
      {analytics.payment_methods.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Métodos de Pago Preferidos por tus Clientes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics.payment_methods.map((method, idx) => {
              const icons = {
                'p2p': '📱',
                'c2p': '💳',
                'bcv_transfer': '🏦',
                'immediate_debit': '💰'
              };
              
              const names = {
                'p2p': 'Pago Móvil P2P',
                'c2p': 'Cobro a Persona',
                'bcv_transfer': 'Transferencia BCV',
                'immediate_debit': 'Débito Inmediato'
              };
              
              return (
                <div key={idx} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-2xl">{icons[method.method] || '💵'}</span>
                    <span className="text-sm font-semibold text-blue-600">{method.percentage}%</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">{names[method.method] || method.method}</p>
                  <p className="text-lg font-bold text-gray-700 mt-2">{formatCurrency(method.volume)}</p>
                  <p className="text-xs text-gray-500">{method.transactions} transacciones</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Órdenes Pendientes Destacadas */}
      {analytics.summary.pending_orders > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex items-center">
            <Activity className="w-6 h-6 text-yellow-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                Tienes {analytics.summary.pending_orders} órdenes pendientes de pago
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                Estas órdenes están esperando confirmación de pago
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Dispersiones Recibidas */}
      {analytics.dispersions.count > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Dispersiones Recibidas</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-sm text-gray-600">Total Recibido</p>
              <p className="text-2xl font-bold text-green-600 mt-2">
                {formatCurrency(analytics.dispersions.total_received)}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-sm text-gray-600">Número de Dispersiones</p>
              <p className="text-2xl font-bold text-blue-600 mt-2">
                {analytics.dispersions.count}
              </p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg text-center">
              <p className="text-sm text-gray-600">Dispersión Promedio</p>
              <p className="text-2xl font-bold text-purple-600 mt-2">
                {formatCurrency(analytics.dispersions.avg_dispersion)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantAnalytics;
